"use client";

import { BrandLogoImg } from "@/app/(dashboard)/_components/shared/brand-logo-img";
import { MiniHintTooltip } from "@/app/(dashboard)/_components/shared/mini-hint-tooltip";
import { api } from "@v1/backend/convex/_generated/api";
import { cn } from "@v1/ui/utils";
import { useConvexAuth, useQuery } from "convex/react";
import { Link2, Moon, Search, Sparkles, Sun } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { type ReactElement, useEffect, useState } from "react";
import { useHelpChrome } from "./help-chrome-context";
import { useHelpAppOrigin, useHelpHref } from "./help-link-base";
import { useHelpTheme } from "./help-theme-shell";

const ICON_BUTTON =
  "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-primary/60 transition-colors hover:bg-muted hover:text-primary/90";

/** ⌘K on Macs, Strg K elsewhere; decided after mount so server and client agree. */
function useShortcutLabel(): string {
  const t = useTranslations("public.helpShell");
  const [mac, setMac] = useState<boolean>(false);
  useEffect(() => setMac(/Mac|iPhone|iPad/.test(navigator.platform)), []);
  return mac ? t("kuerzelMac") : t("kuerzel");
}

/** Opens the search window on ⌘K / Strg K and „/“. */
function useSearchShortcut(setOpen: (open: boolean) => void): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const target = event.target;
      const typing =
        target instanceof HTMLElement && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen(true);
      } else if (event.key === "/" && !typing) {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);
}

/** Search field look-alike that opens the search window (top bar and start page). */
export function HelpSearchTrigger({ size }: { readonly size: "bar" | "hero" }): ReactElement {
  const t = useTranslations("public.helpShell");
  const { setSearchOpen } = useHelpChrome();
  const shortcut = useShortcutLabel();
  return (
    <button
      type="button"
      onClick={() => setSearchOpen(true)}
      className={cn(
        "flex min-w-0 items-center gap-2.5 text-left text-primary/45 transition-colors",
        size === "bar"
          ? "h-10 w-[min(340px,32vw)] rounded-xl bg-muted/70 px-3.5 text-[14px] hover:bg-muted"
          : "mt-6 h-12 w-full rounded-xl border border-border/70 bg-background px-4 text-[15px] shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:border-border",
      )}
    >
      <Search className="h-[18px] w-[18px] shrink-0 text-primary/50" strokeWidth={2} aria-hidden />
      <span className="min-w-0 flex-1 truncate">{t("suchen")}</span>
      <kbd className="hidden shrink-0 rounded-md bg-background/80 px-1.5 py-0.5 font-sans text-[11.5px] text-primary/45 ring-1 ring-border/70 sm:inline">
        {shortcut}
      </kbd>
    </button>
  );
}

function AskAiButton(): ReactElement | null {
  const t = useTranslations("public.helpShell");
  const { ask, chatAvailable } = useHelpChrome();
  if (!chatAvailable) return null;
  return (
    <button
      type="button"
      onClick={() => ask("")}
      className="shrink-0 rounded-xl bg-gradient-to-r from-action-600 via-action-500 to-[#04AC98] p-[1.5px] transition-shadow hover:shadow-[0_0_0_3px_rgba(0,128,112,0.12)]"
    >
      <span className="flex h-[37px] items-center gap-2 rounded-[10.5px] bg-background px-3.5 text-[14px] font-medium text-primary/85">
        <Sparkles className="h-4 w-4 text-action-600" strokeWidth={2} aria-hidden />
        {t("kiFragen")}
      </span>
    </button>
  );
}

/**
 * Help center top bar like Convex docs: logo (or org name) + „Docs“, search,
 * „KI fragen“; right: the org's icon links, sign-in or dashboard, light/dark.
 */
export function HelpHeader({ orgName, orgSlug }: { readonly orgName: string; readonly orgSlug: string }): ReactElement {
  const t = useTranslations("public.helpShell");
  const { setSearchOpen } = useHelpChrome();
  const { theme, toggle } = useHelpTheme();
  const { isAuthenticated } = useConvexAuth();
  const chrome = useQuery(api.pages.public_help.getHelpCenterChrome, { org_slug: orgSlug });
  const helpHref = useHelpHref(orgSlug);
  const appOrigin = useHelpAppOrigin();
  useSearchShortcut(setSearchOpen);
  const accountHref = isAuthenticated
    ? `${appOrigin}/home`
    : chrome?.is_provider === false
      ? `${chrome.portal_origin}/portal`
      : `${appOrigin}/login`;

  return (
    <header className="sticky top-0 z-30 border-b border-border/60 bg-background/95 backdrop-blur">
      <div className="mx-auto flex h-16 w-full max-w-[1536px] items-center gap-4 px-5 md:gap-8">
        <Link
          href={helpHref({ kind: "center" })}
          prefetch={false}
          className="flex min-w-0 shrink-0 items-center gap-2.5 text-primary hover:opacity-85"
        >
          {chrome?.logo_url ? (
            <BrandLogoImg src={chrome.logo_url} alt={orgName} className="block h-7 w-auto max-w-[180px] object-contain" />
          ) : (
            <span className="truncate text-[22px] font-bold leading-tight tracking-tight">{orgName}</span>
          )}
          <span className="text-[24px] font-normal leading-none tracking-tight text-primary/80">{t("docs")}</span>
        </Link>

        <div className="hidden min-w-0 items-center gap-2.5 md:flex">
          <HelpSearchTrigger size="bar" />
          <AskAiButton />
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-1">
          <button type="button" aria-label={t("suchen")} onClick={() => setSearchOpen(true)} className={cn(ICON_BUTTON, "md:hidden")}>
            <Search className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
          </button>
          {(chrome?.links ?? []).map((link) => (
            <MiniHintTooltip key={`${link.url}-${link.label}`} content={link.label || link.url} side="bottom">
              <a href={link.url} target="_blank" rel="noopener noreferrer" aria-label={link.label || link.url} className={cn(ICON_BUTTON, "hidden sm:flex")}>
                {link.icon_url ? (
                  // eslint-disable-next-line @next/next/no-img-element -- org icon from S3 proxy
                  <img src={link.icon_url} alt="" className="h-5 w-5 object-contain" />
                ) : (
                  <Link2 className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
                )}
              </a>
            </MiniHintTooltip>
          ))}
          <a
            href={accountHref}
            className="ml-1.5 inline-flex h-9 shrink-0 items-center rounded-lg bg-primary px-3.5 text-[14px] font-medium text-background transition-opacity hover:opacity-90"
          >
            {isAuthenticated ? t("dashboard") : t("anmelden")}
          </a>
          <button
            type="button"
            onClick={toggle}
            aria-label={theme === "dark" ? t("hell") : t("dunkel")}
            className={cn(ICON_BUTTON, "ml-1")}
          >
            <Sun className="hidden h-[18px] w-[18px] dark:block" strokeWidth={2} aria-hidden />
            <Moon className="h-[18px] w-[18px] dark:hidden" strokeWidth={2} aria-hidden />
          </button>
        </div>
      </div>
    </header>
  );
}
